import React from 'react';
import { Link } from 'react-router-dom';

import { 
  ArrowRight, 
  Award,
  TrendingUp,
  Globe,
  MessageCircle,
  BookOpen,
  Briefcase,
  Ticket
} from 'lucide-react';

const Home = () => {
  const recapStats = [
    { number: '58', label: 'Speakers' },
    { number: '36', label: 'Countries Represented' },
    { number: '790', label: 'Attendees' },
    { number: '220', label: 'Colleges Represented' }
  ];
 



  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 via-white to-secondary-50">
      <div className="relative max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-primary-200/40 blur-3xl" />
        <div className="pointer-events-none absolute right-0 top-10 h-56 w-56 rounded-full bg-secondary-200/50 blur-3xl" />

        {/* Hero Section */}

        <section className="section-shell overflow-hidden mb-16">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-secondary-50" />
          <div className="absolute -right-10 -bottom-10 h-64 w-64 rounded-full bg-primary-200/40 blur-3xl" />
          <div className="absolute -left-10 -top-10 h-52 w-52 rounded-full bg-secondary-200/50 blur-3xl" />

          <div className="relative text-center px-6 py-14 md:py-16">
            <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-4 leading-tight">
              Welcome to <span className="text-primary-700">WECode 2026</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-700 mb-10 max-w-3xl mx-auto">
              Join us in Cambridge, MA this February for the world’s largest student-run undergraduate women in tech conference.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" className="pill-button pill-primary">
                Register with Ticket
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
              <Link to="/events" className="pill-button pill-secondary">
                View Events
              </Link>
              <a
                href="https://www.eventbrite.com/e/harvard-women-engineers-code-wecode-2026-conference-tickets-1841573809839"
                target="_blank"
                rel="noopener noreferrer"
                className="pill-button pill-secondary bg-gradient-to-r from-primary-500 to-secondary-500 text-white border-0 hover:from-primary-600 hover:to-secondary-600 ring-primary-300"
              >
                <Ticket className="h-5 w-5 mr-2" />
                Purchase Tickets
              </a>
            </div>
            <div className="soft-divider mt-10" />
            <p className="text-sm text-primary-800 mt-4 font-medium">
              Inspiring keynotes • Hands-on workshops • Mock interviews • Career fair
            </p>
          </div>
        </section>

        {/* 2024 Recap */}
        <section className="relative mb-16 overflow-hidden rounded-3xl bg-gradient-to-r from-primary-400 via-primary-500 to-secondary-500 text-white shadow-2xl">
          <div className="absolute inset-0 opacity-50" style={{ background: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.35), transparent 35%)' }} />
          <div className="relative px-6 py-12 md:px-12">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-2">WECode 2025 Recap</h2>
            <p className="text-white/85 max-w-2xl mb-10">
              A snapshot of last year’s energy—growing community, world-class speakers, and unforgettable moments.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {recapStats.map((item, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-extrabold drop-shadow-sm">{item.number}</div>
                  <div className="mt-2 text-sm uppercase tracking-wide text-white/80">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Map */}
        {/* Why Attend Section */}
        <section className="section-shell mb-16 p-8 bg-gradient-to-r from-primary-50 to-secondary-50">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Attend WECode?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover the unique benefits of joining our conference this year.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start space-x-4">
              <div className="p-2 bg-primary-100 rounded-lg text-primary-600">
                <TrendingUp className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Career Growth</h3>
                <p className="text-gray-600 text-sm">Advance your career with insights from industry leaders</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="p-2 bg-primary-100 rounded-lg text-primary-600">
                <Globe className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Networking</h3>
                <p className="text-gray-600 text-sm">Connect with peers and potential employers</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="p-2 bg-primary-100 rounded-lg text-primary-600">
                <Award className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Latest Trends</h3>
                <p className="text-gray-600 text-sm">Stay updated with cutting-edge technology trends</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="p-2 bg-primary-100 rounded-lg text-primary-600">
                <BookOpen className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Learning</h3>
                <p className="text-gray-600 text-sm">Hands-on workshops and educational sessions</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="p-2 bg-primary-100 rounded-lg text-primary-600">
                <Briefcase className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Opportunities</h3>
                <p className="text-gray-600 text-sm">Job fair and recruitment opportunities</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="p-2 bg-primary-100 rounded-lg text-primary-600">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Community</h3>
                <p className="text-gray-600 text-sm">Join a vibrant community of tech professionals</p>
              </div>
            </div>
          </div>
        </section>


        {/* Footer */}
        <footer className="border-t border-gray-200 pt-8 pb-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-gray-900 mb-4">WECode 2026</h3>
              <p className="text-gray-600 text-sm">
                The premier technology conference for women in engineering and computer science.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/events" className="text-gray-600 hover:text-primary-600">Events</Link></li>
                <li><Link to="/register" className="text-gray-600 hover:text-primary-600">Register</Link></li>
                <li><Link to="/login" className="text-gray-600 hover:text-primary-600">Login</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>wecodeboard@gmail.com</li>
                <li>Cambridge, MA</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                <a href="https://www.instagram.com/harvardwecode/?hl=en" className="text-gray-600 hover:text-primary-600">
                  <span className="sr-only">Instagram</span>
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4Zm0 2a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H7Zm5 3.5A4.5 4.5 0 1 1 7.5 12 4.5 4.5 0 0 1 12 8.5Zm0 2A2.5 2.5 0 1 0 14.5 13 2.5 2.5 0 0 0 12 10.5Zm4.75-4.25a1.25 1.25 0 1 1-1.25 1.25 1.25 1.25 0 0 1 1.25-1.25Z" />
                  </svg>
                </a>
                <a href="https://www.linkedin.com/company/harvardwecode/" className="text-gray-600 hover:text-primary-600">
                  <span className="sr-only">LinkedIn</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="text-center text-sm text-gray-600">
            <p>&copy; 2026 WECode Conference. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Home; 